import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../types/Customer';
export function CustomerDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  // Mock data - in a real app, this would be fetched from an API
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+94 77 123 4567',
    address: '123 Business Street',
    city: 'Colombo',
    country: 'Sri Lanka',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-600',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`} data-id="element-3855">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCardIcon = (type: string) => {
    return <CreditCardIcon size={20} data-id="element-3856" />;
  };
  return <div className="space-y-6" data-id="element-3857">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3858">
        <button onClick={() => navigate('/customers')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3859">
          <ArrowLeftIcon size={20} data-id="element-3860" />
          Back to Customers
        </button>
        <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3861">
          <EditIcon size={18} data-id="element-3862" />
          Edit Customer
        </button>
      </div>
      {/* Customer Info Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3863">
        <div className="flex items-start justify-between" data-id="element-3864">
          <div className="flex items-start gap-4" data-id="element-3865">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xl" data-id="element-3866">
              {customer.name.charAt(0)}
            </div>
            <div data-id="element-3867">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3868">
                {customer.name}
              </h1>
              <div className="mt-2 space-y-1" data-id="element-3869">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3870">
                  <MailIcon size={16} data-id="element-3871" />
                  {customer.email}
                </div>
                {customer.phone && <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3872">
                    <PhoneIcon size={16} data-id="element-3873" />
                    {customer.phone}
                  </div>}
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3874">
                  <MapPinIcon size={16} data-id="element-3875" />
                  {customer.address}, {customer.city}, {customer.country}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-3876">
            {getStatusBadge(customer.status)}
            <div className="mt-2 text-sm text-gray-500" data-id="element-3877">
              Customer since{' '}
              {new Date(customer.createdAt).toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3878">
          <div data-id="element-3879">
            <div className="text-sm text-gray-500" data-id="element-3880">Total Spent</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3881">
              Rs. {customer.totalSpent.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-3882">
            <div className="text-sm text-gray-500" data-id="element-3883">Total Invoices</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3884">
              {customer.invoiceCount}
            </div>
          </div>
          <div data-id="element-3885">
            <div className="text-sm text-gray-500" data-id="element-3886">Avg. Invoice Value</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3887">
              Rs.{' '}
              {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-LK')}
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3888">
        <div className="border-b border-gray-200" data-id="element-3889">
          <nav className="flex -mb-px" data-id="element-3890">
            <button onClick={() => setActiveTab('invoices')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'invoices' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3891">
              <div className="flex items-center gap-2" data-id="element-3892">
                <FileTextIcon size={18} data-id="element-3893" />
                Invoices
              </div>
            </button>
            <button onClick={() => setActiveTab('cards')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'cards' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3894">
              <div className="flex items-center gap-2" data-id="element-3895">
                <CreditCardIcon size={18} data-id="element-3896" />
                Payment Methods
              </div>
            </button>
            <button onClick={() => setActiveTab('transactions')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'transactions' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3897">
              <div className="flex items-center gap-2" data-id="element-3898">
                <ActivityIcon size={18} data-id="element-3899" />
                Transactions
              </div>
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-3900">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && <div data-id="element-3901">
              <div className="overflow-x-auto" data-id="element-3902">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3903">
                  <thead data-id="element-3904">
                    <tr data-id="element-3905">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3906">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3907">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3908">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3909">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3910">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-3911">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3912">
                    {customerInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-3913">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900" data-id="element-3914">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3915">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3916">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-3917">
                          Rs. {invoice.amount.toLocaleString('en-LK')}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3918">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-right" data-id="element-3919">
                          <button onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="text-blue-600 hover:text-blue-900" data-id="element-3920">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {/* Payment Methods Tab */}
          {activeTab === 'cards' && <div className="space-y-4" data-id="element-3921">
              <div className="flex justify-between items-center mb-4" data-id="element-3922">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3923">
                  Stored Payment Methods
                </h3>
                <button className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-3924">
                  <PlusIcon size={18} data-id="element-3925" />
                  Add Card
                </button>
              </div>
              {paymentMethods.map(method => <div key={method.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-3926">
                  <div className="flex items-center justify-between" data-id="element-3927">
                    <div className="flex items-center gap-4" data-id="element-3928">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-3929">
                        {getCardIcon(method.type)}
                      </div>
                      <div data-id="element-3930">
                        <div className="flex items-center gap-2" data-id="element-3931">
                          <span className="text-sm font-medium text-gray-900" data-id="element-3932">
                            {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                            •••• {method.last4}
                          </span>
                          {method.isDefault && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded" data-id="element-3933">
                              Default
                            </span>}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3934">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3935">
                          {method.cardholderName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3936">
                      {!method.isDefault && <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded" data-id="element-3937">
                          Set as Default
                        </button>}
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded" data-id="element-3938">
                        <TrashIcon size={18} data-id="element-3939" />
                      </button>
                    </div>
                  </div>
                </div>)}
              {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-3940">
                  No payment methods stored
                </div>}
            </div>}
          {/* Transactions Tab */}
          {activeTab === 'transactions' && <div data-id="element-3941">
              <div className="overflow-x-auto" data-id="element-3942">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3943">
                  <thead data-id="element-3944">
                    <tr data-id="element-3945">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3946">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3947">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3948">
                        Payment Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3949">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3950">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3951">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50" data-id="element-3952">
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3953">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-3954">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={() => navigate(`/invoice-detail/${transaction.invoiceId}`)} className="ml-2 text-blue-600 hover:text-blue-900" data-id="element-3955">
                              View Invoice
                            </button>}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3956">
                          {transaction.paymentMethod || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3957">
                          <span className={transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'} data-id="element-3958">
                            {transaction.amount < 0 ? '-' : ''}Rs.{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-LK')}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3959">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}